import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Building2, Send, Mail, Loader2, Plus, Trash2, Edit2, Save, X, Check,
  AlertTriangle, ExternalLink, ChevronDown, ChevronUp
} from "lucide-react";
import { Button } from "../components/ui/button";
import MobileNav from "../components/MobileNav";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../components/ui/collapsible";

const API = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_SECTIONS = [
  {
    id: "loans",
    title: "CREDIT BUILDER LOANS",
    icon: "🧱",
    color: "cyan",
    items: [
      { id: "self", name: "Self Financial", url: "https://www.self.inc", desc: "Sign up → choose a monthly plan ($25–$150). You make monthly payments into a locked savings account. They report it as a loan to all 3 bureaus. After completion, you get the money back. Optional: upgrade to a secured credit card." },
      { id: "creditstrong", name: "Credit Strong", url: "https://www.creditstrong.com", desc: "Choose a plan (longer term = stronger impact). Monthly payments go into a savings account. Reports a high-value loan tradeline. Best option for building strong profile history." },
      { id: "kikoff", name: "Kikoff", url: "https://www.kikoff.com", desc: "Sign up (no credit check). You get a small credit line (usually $500). You \"purchase\" items from their store (no real spending needed). They report low utilization automatically." },
      { id: "seedfi", name: "SeedFi", url: "https://www.seedfi.com", desc: "Choose \"Borrow & Grow\" plan. Part of your payment goes to savings. Reports a loan tradeline monthly. You unlock saved money at the end." },
      { id: "moneylion", name: "MoneyLion", url: "https://www.moneylion.com", desc: "Sign up for Credit Builder Plus. Small loan is opened and paid monthly. Reports to bureaus. Also gives access to cash advances if needed." },
      { id: "brigit", name: "Brigit", url: "https://www.hellobrigit.com", desc: "Join membership. Opens a small credit builder loan. Reports monthly payments. Also provides overdraft protection." },
    ]
  },
  {
    id: "secured",
    title: "SECURED + EASY APPROVAL REVOLVING",
    icon: "💳",
    color: "purple",
    items: [
      { id: "capitalone", name: "Capital One Secured", url: "https://www.capitalone.com/credit-cards/secured/", desc: "Apply → soft pull first (no damage). Deposit ($200–$500) = your credit limit. Use card monthly and keep balance low. Pays back deposit later if upgraded." },
      { id: "opensky", name: "OpenSky", url: "https://www.openskycc.com", desc: "No credit check. Deposit = credit limit. Guaranteed approval. Use for small purchases and pay off monthly." },
      { id: "chime", name: "Chime Credit Builder", url: "https://www.chime.com/credit-builder/", desc: "Open Chime account first. Transfer money into Credit Builder account. Spend only what you deposit. They report it as on-time payments." },
      { id: "sable", name: "Sable", url: "https://www.sablecard.com", desc: "Sign up and deposit funds. Works like a secured debit/credit hybrid. Reports as a credit card. Can graduate to unsecured." },
      { id: "ava", name: "AVA Finance", url: "https://www.meetava.com", desc: "Connect your bank account. Pay a small monthly fee. Reports rent-style payments as a tradeline. No deposit required." },
      { id: "grow", name: "Grow Credit", url: "https://www.growcredit.com", desc: "Choose a plan (free or paid). Connect subscriptions (Netflix, etc.). They pay subscription → you repay monthly. Reports as revolving credit." },
    ]
  },
  {
    id: "nocreditcheck",
    title: "NO CREDIT CHECK / ALT CARDS",
    icon: "💳",
    color: "green",
    items: [
      { id: "tomo", name: "TomoCredit", url: "https://tomocredit.com", desc: "No credit check. Connect bank account instead. Weekly autopay required. Reports as a credit card." },
      { id: "extra", name: "Extra", url: "https://extra.app", desc: "Link your debit card. Use Extra card like debit. They report purchases as credit usage. Auto-pay from your bank." },
      { id: "petal", name: "Petal", url: "https://www.petalcard.com", desc: "Uses banking history instead of credit score. May approve without strong credit. Works like a normal credit card. Pay balance monthly." },
      { id: "missionlane", name: "Mission Lane", url: "https://www.missionlane.com", desc: "Pre-qualification with soft pull. May offer unsecured card. Reports to all bureaus. Good for rebuilding." },
      { id: "avant", name: "Avant", url: "https://www.avant.com", desc: "Offers credit cards + loans. Pre-qualify first (soft check). Higher approval odds than traditional banks." },
    ]
  },
  {
    id: "boost",
    title: "CREDIT BOOST / REPORTING",
    icon: "🧠",
    color: "pink",
    items: [
      { id: "experianboost", name: "Experian Boost", url: "https://www.experian.com/boost", desc: "Connect bank account. Adds utilities + subscriptions. Instant update to Experian score." },
      { id: "stellarfi", name: "StellarFi", url: "https://www.stellarfi.com", desc: "Connect bills (phone, utilities, etc.). They report each as tradelines. Multiple accounts added at once." },
    ]
  },
  {
    id: "rent",
    title: "RENT REPORTING (VERY IMPORTANT)",
    icon: "🏠",
    color: "orange",
    items: [
      { id: "esusu", name: "Esusu", url: "https://www.esusurent.com", desc: "Reports rent payments. Usually works through landlord/property." },
      { id: "rentreporters", name: "RentReporters", url: "https://www.rentreporters.com", desc: "Can add past rent history. Verifies with landlord. Reports to bureaus." },
      { id: "levelcredit", name: "LevelCredit", url: "https://www.levelcredit.com", desc: "Connect rent + utility payments. Reports both. Builds multiple lines." },
      { id: "rentalkharma", name: "Rental Kharma", url: "https://www.rentalkharma.com", desc: "Verifies rent. Reports to TransUnion." },
      { id: "boom", name: "Boom", url: "https://www.boompay.app", desc: "Connect rent payments. Reports monthly." },
      { id: "pinata", name: "Piñata", url: "https://www.pinata.ai", desc: "Rent reporting + rewards system. Earn points while building credit." },
      { id: "bilt", name: "Bilt Rewards", url: "https://www.biltrewards.com", desc: "Pay rent through their system. Reports as credit activity. Earn points." },
    ]
  },
  {
    id: "backdate",
    title: "BACKDATE / ALT HISTORY BUILDERS",
    icon: "🔁",
    color: "blue",
    items: [
      { id: "credable", name: "Credable", url: "https://www.credable.com", desc: "Adds tradeline-style history. Helps strengthen profile." },
      { id: "perch", name: "Perch", url: "https://www.perchcredit.com", desc: "Connect bills you already pay. Reports them as credit history." },
      { id: "ecredable", name: "eCredable", url: "https://www.ecredable.com", desc: "Adds utilities + rent. Works with lenders directly." },
    ]
  },
  {
    id: "utilities",
    title: "UTILITIES / BANK DATA",
    icon: "⚡",
    color: "yellow",
    items: [
      { id: "ecredablelift", name: "eCredable Lift", url: "https://www.ecredable.com/lift", desc: "Adds utility payment history. Works especially for mortgage approvals." },
      { id: "ultrafico", name: "UltraFICO", url: "https://www.fico.com/en/products/ultrafico", desc: "Connect bank account. Uses savings + cash flow data. Can increase approval odds." },
    ]
  },
];

const COLOR_MAP = {
  cyan: { bg: "bg-cyan-500/10", border: "border-cyan-500/30", text: "text-cyan-400", accent: "#00d9ff" },
  purple: { bg: "bg-purple-500/10", border: "border-purple-500/30", text: "text-purple-400", accent: "#a855f7" },
  green: { bg: "bg-green-500/10", border: "border-green-500/30", text: "text-green-400", accent: "#4ade80" },
  pink: { bg: "bg-pink-500/10", border: "border-pink-500/30", text: "text-pink-400", accent: "#ec4899" },
  orange: { bg: "bg-orange-500/10", border: "border-orange-500/30", text: "text-orange-400", accent: "#f97316" },
  blue: { bg: "bg-blue-500/10", border: "border-blue-500/30", text: "text-blue-400", accent: "#3b82f6" },
  yellow: { bg: "bg-yellow-500/10", border: "border-yellow-500/30", text: "text-yellow-400", accent: "#fbbf24" },
};

export default function CreditBuilderListPage() {
  const [sections, setSections] = useState(DEFAULT_SECTIONS);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailData, setEmailData] = useState({ client_email: "" });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [editingItem, setEditingItem] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [addingSectionId, setAddingSectionId] = useState(null);
  const [newItem, setNewItem] = useState({ name: "", url: "", desc: "" });
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSections();
  }, []);

  const loadSections = async () => {
    try {
      const response = await axios.get(`${API}/api/credit-builder-config`);
      if (response.data?.sections) {
        setSections(response.data.sections);
      }
    } catch (error) {
      console.log("Using default sections");
    }
  };

  const saveSections = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/credit-builder-config`, { sections });
      toast.success("Credit builder list saved!");
      setHasChanges(false);
    } catch (error) {
      console.error("Error saving:", error);
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleSendEmail = async () => {
    if (!emailData.client_email || !emailData.client_email.includes('@')) {
      toast.error("Please enter a valid email address");
      return;
    }
    
    setSendingEmail(true);
    try {
      await axios.post(`${API}/api/credit-builder-list/send-email`, emailData);
      toast.success(`Credit Builder List sent to ${emailData.client_email}!`);
      setShowEmailModal(false);
      setEmailData({ client_email: "" });
    } catch (error) {
      console.error("Error sending email:", error);
      toast.error("Failed to send email");
    } finally {
      setSendingEmail(false);
    }
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const handleEditItem = (sectionId, item) => {
    setEditingItem({ sectionId, itemId: item.id });
    setEditForm({ ...item });
  };

  const handleSaveEdit = () => {
    setSections(prev => prev.map(section => {
      if (section.id === editingItem.sectionId) {
        return {
          ...section,
          items: section.items.map(item => 
            item.id === editingItem.itemId ? { ...editForm } : item
          )
        };
      }
      return section;
    }));
    setEditingItem(null);
    setEditForm({});
    setHasChanges(true);
  };

  const handleDeleteItem = (sectionId, itemId) => {
    setSections(prev => prev.map(section => {
      if (section.id === sectionId) {
        return {
          ...section,
          items: section.items.filter(item => item.id !== itemId)
        };
      }
      return section;
    }));
    setHasChanges(true);
    toast.success("Item removed");
  };

  const handleAddItem = () => {
    if (!newItem.name || !newItem.url) {
      toast.error("Name and URL are required");
      return;
    }
    
    setSections(prev => prev.map(section => {
      if (section.id === addingSectionId) {
        return {
          ...section,
          items: [...section.items, { ...newItem, id: `custom_${Date.now()}` }]
        };
      }
      return section;
    }));
    
    setShowAddModal(false);
    setNewItem({ name: "", url: "", desc: "" });
    setAddingSectionId(null);
    setHasChanges(true);
    toast.success("Item added!");
  };

  const totalServices = sections.reduce((acc, section) => acc + section.items.length, 0);

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      <MobileNav />
      
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-green-500 to-cyan-500 rounded-lg">
              <Building2 className="w-6 h-6" />
            </div>
            Credit Builder Master List
          </h1>
          <p className="text-zinc-400 mt-1">{totalServices} services with step-by-step instructions</p>
        </div>
        
        <div className="flex gap-3">
          <Button
            onClick={saveSections}
            disabled={saving || !hasChanges}
            className={`${hasChanges ? 'bg-green-600 hover:bg-green-700' : 'bg-zinc-700'}`}
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            {hasChanges ? "Save Changes" : "Saved"}
          </Button>
          <Button
            onClick={() => setShowEmailModal(true)}
            className="bg-gradient-to-r from-green-600 to-cyan-600 hover:from-green-700 hover:to-cyan-700"
          >
            <Mail className="w-4 h-4 mr-2" />
            Email to Client
          </Button>
        </div>
      </div>

      {/* Warning Banner */}
      <div className="bg-gradient-to-r from-red-500/20 via-orange-500/20 to-yellow-500/20 border-2 border-red-500/50 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-400 font-bold text-lg">⚠️ READ FIRST (IMPORTANT)</p>
            <p className="text-zinc-300 mt-2">Do <strong className="text-red-400">NOT</strong> apply for anything that:</p>
            <ul className="text-zinc-300 mt-2 space-y-1 ml-4">
              <li>• Runs a <strong className="text-red-400">hard inquiry</strong></li>
              <li>• Requires good credit approval</li>
              <li>• Is not secured or guaranteed</li>
            </ul>
            <p className="text-green-400 mt-3 font-medium">👉 Follow the steps below exactly. These accounts are safe, strategic, and designed to build your credit while we repair it.</p>
          </div>
        </div>
      </div>

      {/* Credit Builder Sections */}
      {sections.map((section) => {
        const colors = COLOR_MAP[section.color] || COLOR_MAP.cyan;
        const isExpanded = expandedSections[section.id] !== false; // Default to expanded
        
        return (
          <Card key={section.id} className={`bg-zinc-900 border ${colors.border}`}>
            <Collapsible open={isExpanded} onOpenChange={() => toggleSection(section.id)}>
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-zinc-800/50 transition-colors">
                  <div className="flex items-center justify-between">
                    <CardTitle className={`text-lg ${colors.text} flex items-center gap-2`}>
                      <span className="text-xl">{section.icon}</span>
                      {section.title}
                      <span className="text-xs text-zinc-500 ml-2">({section.items.length})</span>
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          setAddingSectionId(section.id);
                          setShowAddModal(true);
                        }}
                        className="text-zinc-400 hover:text-green-400"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                      {isExpanded ? <ChevronUp className="w-5 h-5 text-zinc-400" /> : <ChevronDown className="w-5 h-5 text-zinc-400" />}
                    </div>
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="space-y-4">
                  {section.items.map((item) => (
                    <div
                      key={item.id}
                      className={`${colors.bg} ${colors.border} border rounded-lg p-4 relative`}
                    >
                      {editingItem?.sectionId === section.id && editingItem?.itemId === item.id ? (
                        // Edit Mode
                        <div className="space-y-3">
                          <Input
                            value={editForm.name}
                            onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                            placeholder="Service Name"
                            className="bg-zinc-800 border-zinc-600"
                          />
                          <Input
                            value={editForm.url}
                            onChange={(e) => setEditForm({...editForm, url: e.target.value})}
                            placeholder="URL"
                            className="bg-zinc-800 border-zinc-600"
                          />
                          <Textarea
                            value={editForm.desc}
                            onChange={(e) => setEditForm({...editForm, desc: e.target.value})}
                            placeholder="Description / Instructions"
                            className="bg-zinc-800 border-zinc-600 min-h-[100px]"
                          />
                          <div className="flex gap-2">
                            <Button size="sm" onClick={handleSaveEdit} className="bg-green-600 hover:bg-green-700">
                              <Check className="w-4 h-4 mr-1" /> Save
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setEditingItem(null)} className="border-zinc-600">
                              <X className="w-4 h-4 mr-1" /> Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        // View Mode
                        <>
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <a
                                href={item.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`text-white font-bold text-lg hover:${colors.text} transition-colors flex items-center gap-2`}
                              >
                                {item.name}
                                <ExternalLink className="w-4 h-4 opacity-50" />
                              </a>
                              <p className="text-zinc-500 text-xs mt-1 break-all">{item.url}</p>
                            </div>
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleEditItem(section.id, item)}
                                className="text-zinc-400 hover:text-blue-400 h-8 w-8 p-0"
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDeleteItem(section.id, item.id)}
                                className="text-zinc-400 hover:text-red-400 h-8 w-8 p-0"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          <p className="text-zinc-300 mt-3 text-sm leading-relaxed whitespace-pre-line">{item.desc}</p>
                        </>
                      )}
                    </div>
                  ))}
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>
        );
      })}

      {/* Final Game Plan */}
      <Card className="bg-gradient-to-br from-green-500/10 via-cyan-500/10 to-purple-500/10 border-2 border-green-500/50">
        <CardHeader>
          <CardTitle className="text-xl text-green-400 flex items-center gap-2">
            🔥 FINAL GAME PLAN
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="space-y-3 text-zinc-300">
            <li className="flex items-start gap-3">
              <span className="bg-green-500 text-black font-bold rounded-full w-6 h-6 flex items-center justify-center text-sm flex-shrink-0">1</span>
              <span>Start with <strong className="text-white">2–4 accounts max</strong></span>
            </li>
            <li className="flex items-start gap-3">
              <span className="bg-green-500 text-black font-bold rounded-full w-6 h-6 flex items-center justify-center text-sm flex-shrink-0">2</span>
              <span>Use them every month <strong className="text-white">(small amounts only)</strong></span>
            </li>
            <li className="flex items-start gap-3">
              <span className="bg-green-500 text-black font-bold rounded-full w-6 h-6 flex items-center justify-center text-sm flex-shrink-0">3</span>
              <span>Keep balances <strong className="text-cyan-400">under 10%</strong></span>
            </li>
            <li className="flex items-start gap-3">
              <span className="bg-green-500 text-black font-bold rounded-full w-6 h-6 flex items-center justify-center text-sm flex-shrink-0">4</span>
              <span>Pay on time <strong className="text-white">every single month</strong></span>
            </li>
          </ol>
          <div className="mt-6 p-4 bg-zinc-900/50 rounded-lg border border-green-500/30">
            <p className="text-green-400">👉 This builds strong positive history while we remove negatives</p>
            <p className="text-cyan-400 mt-2">👉 This is how you <strong>increase your score the fastest</strong></p>
          </div>
        </CardContent>
      </Card>

      {/* Add Item Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-green-400" />
              Add New Service
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-zinc-400">Service Name *</Label>
              <Input
                value={newItem.name}
                onChange={(e) => setNewItem({...newItem, name: e.target.value})}
                placeholder="Service Name"
                className="mt-1 bg-zinc-800 border-zinc-700"
              />
            </div>
            <div>
              <Label className="text-zinc-400">URL *</Label>
              <Input
                value={newItem.url}
                onChange={(e) => setNewItem({...newItem, url: e.target.value})}
                placeholder="https://example.com"
                className="mt-1 bg-zinc-800 border-zinc-700"
              />
            </div>
            <div>
              <Label className="text-zinc-400">Description / Instructions</Label>
              <Textarea
                value={newItem.desc}
                onChange={(e) => setNewItem({...newItem, desc: e.target.value})}
                placeholder="Step-by-step instructions..."
                className="mt-1 bg-zinc-800 border-zinc-700 min-h-[100px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)} className="border-zinc-700">
              Cancel
            </Button>
            <Button onClick={handleAddItem} disabled={!newItem.name || !newItem.url} className="bg-green-600 hover:bg-green-700">
              <Plus className="w-4 h-4 mr-2" /> Add Service
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Modal */}
      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-green-400" />
              Send Credit Builder List
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Send the complete credit builder master list with all {totalServices} services and detailed instructions.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-zinc-400">Client Email *</Label>
              <Input
                type="email"
                value={emailData.client_email}
                onChange={(e) => setEmailData({...emailData, client_email: e.target.value})}
                placeholder="client@example.com"
                className="mt-1 bg-zinc-800 border-zinc-700"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailModal(false)} className="border-zinc-700">
              Cancel
            </Button>
            <Button 
              onClick={handleSendEmail}
              disabled={sendingEmail || !emailData.client_email}
              className="bg-green-600 hover:bg-green-700"
            >
              {sendingEmail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
